/**
 * Vendor Quote upload → proposal flow — shared types.
 *
 * Shapes follow the planned API in VENDOR_QUOTE_UPLOAD_IMPLEMENTATION_PLAN.md (project root).
 * Until the backend exists these are filled by VendorQuoteFlowMockService (API-SWAP).
 */

/**
 * Where a value on the Review screen came from: read from the document (✦), worked out by us from
 * what the document gave (calculator — e.g. unit price = line total ÷ qty), or changed by the user (✎).
 */
export type FieldSource = 'document' | 'calculated' | 'manual';

export interface ExtractedField<T = string> {
  value: T;
  /** The value as first read or calculated — kept so setting it back restores its source. */
  original: T;
  source: FieldSource;
  /** How a 'calculated' value was worked out, e.g. "Not sent as a unit price. Line total $97.34 ÷ 156". */
  basis?: string | null;
  /** 0..1 confidence reported by the reader. */
  confidence?: number;
}

export interface ExtractedVendor {
  name: ExtractedField;
  contact: ExtractedField;
  email: ExtractedField;
  phone: ExtractedField;
  address: ExtractedField;
}

/** Result of the vendor check (C1 / C2). */
export interface VendorMatch {
  /** true → "Vendor already in the system"; false → "This vendor is not in the system". */
  exists: boolean;
  vendorId?: number;
  vendorName?: string;
  /** What matched an existing vendor, e.g. 'email'. */
  matchedBy?: 'email' | 'phone' | 'name';
  matchedValue?: string;
  /** Number of vendors that were searched (shown in the "no match among N vendors" note). */
  searchedCount: number;
}

/** One line read from the document. */
export interface ExtractedLine {
  lineNo: number;
  /** Text exactly as printed in the document (shown in the preview). */
  documentText: string;
  /** Product code read from the line (e.g. "1100"). */
  code: string;
  name: string;
  description: string;
  qty: ExtractedField<number>;
  unit: string;
  unitPrice: ExtractedField<number>;
  /** Line total as printed on the document (display / cross-check only), or null when it has none. */
  lineTotal?: number | null;
  /** The line counts at the document's printed total (lineTotal) instead of qty × unit price. */
  useDocTotal?: boolean;
  /** Low-confidence value → yellow row + "Check this". */
  needsCheck: boolean;
  /** Matched catalog product, or null when it is not in the catalog (NEW PRODUCT). */
  matchedProduct: { id: number; title: string } | null;
  /** A credit / discount line (negative qty or price): shown, but left out of the saved quote. */
  rejected?: boolean;
}

export type ExtractionStatus = 'queued' | 'reading' | 'vendor' | 'matching' | 'checking' | 'ready' | 'failed' | 'cancelled' | 'saved';

/** The draft quote (vendor_quote_extractions row in the planned API). */
export interface VendorQuoteDraft {
  id: number;
  bidId: number;
  status: ExtractionStatus;
  file: { name: string; size: string; mime: string; url?: string };
  vendor: ExtractedVendor;
  vendorMatch: VendorMatch;
  /** Set once the vendor is confirmed (added or existing). */
  vendorId?: number;
  vendorIsNew?: boolean;
  quoteNumber: ExtractedField;
  quoteDate: ExtractedField;
  validTill: ExtractedField;
  terms: string;
  freight: number;
  /** Set when we worked freight out (the document has no freight field): how, for the Review label. */
  freightBasis?: string | null;
  documentTotal: number;
  /** Where the saved quote's total comes from: lines + freight, the document's total, or typed by the user. */
  quoteTotalSource?: 'calculated' | 'document' | 'manual';
  /** The user's own total — only used when quoteTotalSource is 'manual'. */
  quoteTotal?: number | null;
  lines: ExtractedLine[];
  /** scopes.id values chosen on the Review screen (required to save) — ids, not names: the
   *  quote↔scope pivot stores ids, and scope names are neither unique nor stable. */
  scopes: number[];
  /** Scopes each matched product is in now ({productId: [{id, label}]}) — Review pre-selects the bid's scopes from these. */
  matchedProductScopes?: Record<string, Array<{ id: number; label: string }>>;
}

/** Progress stages shown in the upload dialog's reading checklist (B3). */
export const READING_STAGES: Array<{ stage: ExtractionStatus; label: string }> = [
  { stage: 'reading', label: 'Reading the document' },
  { stage: 'vendor', label: 'Finding the vendor' },
  { stage: 'matching', label: 'Matching materials to your products' },
  { stage: 'checking', label: 'Checking quantities, prices and totals' },
];

/** A quote saved through the upload flow, as the Vendor Quotes list shows it. */
export interface SavedUploadedQuote {
  id: number;
  bidId: number;
  vendorName: string;
  quotationReference: string;
  validTill: string;
  createdAt: string;
  source: 'uploaded';
  scopes: string[];
  freight: number;
  items: Array<{ productId: number | null; name: string; qty: number; unit: string; unitPrice: number }>;
}

/* ── Proposal side (E–H) ─────────────────────────────────────────────── */

/** One quote item that can price a proposal material (F2). */
export interface QuoteOption {
  /** `${quoteId}:${quoteItemId}` — stable key for selections. */
  optionKey: string;
  quoteId: number;
  quoteItemId: number;
  vendorId: number;
  vendorName: string;
  quotationReference: string;
  validTill: string;
  isExpired: boolean;
  /** False when the quote line has no unit price (null or 0) — offered but not pickable, so it can't zero a cost. */
  hasPrice: boolean;
  itemName: string;
  itemDescription: string;
  qty: number;
  unit: string;
  unitPrice: number;
  /** The proposal product this item prices; a synthetic negative id when the quote adds a product the scope doesn't have. */
  productId: number;
}

/** A whole quote on the bid, for "Apply Vendor Quotes to Scope" (G1). */
export interface QuoteSummary {
  quoteId: number;
  vendorId: number;
  vendorName: string;
  quotationReference: string;
  validTill: string;
  isExpired: boolean;
  /** Sum of the items plus freight. */
  total: number;
  items: QuoteOption[];
}

/** Snapshot of a proposal line used by Compare / Apply / Undo. */
export interface ProposalLineState {
  id: number | null;
  productId: number;
  tag: string;
  name: string;
  description: string;
  vendorId: number | null;
  vendorName: string;
  qty: number;
  unit: string;
  unitCost: number;
  profitPct: number;
  /** Quote code the line was priced from (E2 source chip). */
  quoteSource?: string;
}

export type ChangeKind = 'changed' | 'same' | 'added' | 'missing';

/** One row of "Compare before you apply" (H2). */
export interface PlannedChange {
  kind: ChangeKind;
  before: ProposalLineState | null;
  after: ProposalLineState | null;
  /** "lowest of N quotes" note. */
  note?: string;
  /** Only for kind 'missing': the user's Keep / Remove choice (Keep is the default). */
  remove?: boolean;
}

export interface ApplyPlan {
  /** Where the plan came from, so Back on Compare reopens the right picker. */
  from: 'material' | 'scope';
  label: string;
  changes: PlannedChange[];
}

/** Selection made in "Select Vendor Quote": option key → quantity from that vendor (F3). */
export type MaterialSelection = Map<string, number>;

/** What the server builds for Compare: the plan plus cost/sell totals before and after. */
export interface ServerApplyPlan extends ApplyPlan {
  totals: { before: { cost: number; sell: number }; after: { cost: number; sell: number } };
}

/**
 * Body of preview / apply — the user's choices only; the server rebuilds the plan and its prices.
 * The line is `proposal_scope_item_id` on a proposal and `item_id` (a change_order_items id) on a change order.
 */
export type VendorQuoteApplyRequest = (
  | { mode: 'material'; proposal_scope_item_id?: number; item_id?: number; picks: Array<{ quote_item_id: number; quantity?: number }> }
  | { mode: 'scope'; quote_ids: number[]; remove_item_ids?: number[] }
) & { /** The user turned on "Include expired quotes"; the server refuses expired quotes otherwise. */ allow_expired?: boolean };

/** Which kind of scope the vendor quote endpoints act on. */
export type VendorQuoteTarget = 'proposal' | 'change-order';

/** One apply on a scope, as stored for Undo (proposal_vendor_quote_applications). */
export interface VendorQuoteApplication {
  id: number;
  source: 'material' | 'scope';
  label: string;
  counts: { changed: number; added: number; removed: number; sellBefore?: number; sellAfter?: number } | null;
  /** Lines as they were before (and after) the apply; `before`/`after` are proposal_scope_items columns. */
  snapshot: {
    changed: Array<{ id: number; before: Record<string, any>; after: Record<string, any> }>;
    added: Array<{ id: number; after: Record<string, any> }>;
    removed: number[];
  };
}

/**
 * One apply still in effect on a scope, as the Apply dialog lists it (GET …/vendor-quotes/applications).
 * `source: 'scope'` = Apply Vendor Quotes to Scope (quoteIds = the quotes picked); 'material' = Select
 * Vendor Quote on one line (lines = the line names it priced).
 */
export interface VendorQuoteAppliedRow {
  id: number;
  source: 'material' | 'scope';
  label: string;
  quoteIds: number[];
  lines: string[];
  counts: { changed: number; added: number; removed: number } | null;
  appliedAt: string | null;
  appliedBy: string | null;
  /** False when a line it set was changed since — undo would overwrite that change. */
  canUndo: boolean;
  undoBlockedReason: string | null;
}

/** Line as the server returns it after apply/undo — ProposalLineState plus the quote it came from. */
export interface ServerProposalLine extends ProposalLineState {
  quoteId: number | null;
}

export interface VendorQuoteApplyResult {
  application: VendorQuoteApplication | null;
  lines: ServerProposalLine[];
  /** Same totals payload the line save endpoints return (CostDrillDownResource). */
  jobValue: any;
}

/** Per-line marks the Quotes tab shows after Apply (E4). */
export type LineStatus = 'changed' | 'added' | 'removed';

/** What the Quotes tab shows in the banner after Apply (E4). Per-line marks are kept next to the form lines. */
export interface AppliedState {
  label: string;
  counts: string;
  beforeSell: number;
  afterSell: number;
}
